import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useSlotGroup,
  usePropertyAddressUrl,
  EditableCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { clean, hoursRows, stagger, telHref, type OfficeHour } from '../utils/quinn';
import { useRawProperty } from '../utils/hooks';
import { PATHS, tourCta, useSiteFlags } from '../utils/site';

const plansCtaDef: SlotGroupDefinition = {
  label: 'Floor Plans Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'View Floor Plans' },
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const tourCtaDef: SlotGroupDefinition = {
  label: 'Tour Button',
  description: 'Without a self-guided tour URL on the property, the default button becomes Contact Us.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Schedule a Tour' },
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'self_guided_tour_clicked' },
  },
};

const infoRow =
  'flex min-w-0 flex-col gap-1 [container-type:inline-size] [&_span]:text-[13px] [&_span]:font-medium [&_span]:leading-[18px] [&_span]:text-[--q-muted]';
const infoLink =
  'self-start whitespace-nowrap text-base font-medium leading-6 text-[--q-ink] no-underline hover:underline hover:underline-offset-[3px]';

const WEEK = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

/** Thank-you page body in the Contact Us layout: the confirmation in a card, the leasing office beside it, then the map. */
export function ThankYou() {
  const p = useRawProperty();
  const flags = useSiteFlags();
  const directions = usePropertyAddressUrl();
  const plans = useSlotGroup('thankyou.plans_cta', plansCtaDef);
  const tour = useSlotGroup('thankyou.tour_cta', tourCtaDef);
  const hours = useCollectionBinding<OfficeHour>('thankyou.hours', { collection: 'office_hours' });

  const tourLink = tourCta(tour, flags.selfTourUrl, { url: PATHS.selfTour, label: 'Schedule a Tour' });
  const tel = telHref(flags.phone);
  const street = clean(p.property_address_1);
  const city = [[clean(p.property_address_city), clean(p.property_address_state)].filter(Boolean).join(', '), clean(p.property_address_zip)].filter(Boolean).join(' ');
  const address = [street, city].filter(Boolean).join(', ');
  const rows = hoursRows([...hours].sort((a, b) => WEEK.indexOf(a.day || '') - WEEK.indexOf(b.day || '')), false);
  const mapSrc = address ? `https://maps.google.com/maps?q=${encodeURIComponent(`${clean(p.property_name) || ''} ${address}`.trim())}&z=15&output=embed` : null;

  return (
    <section
      className="section [&.section]:pb-4xl [&.section]:pt-20 max-[767px]:[&.section]:pb-14 max-[767px]:[&.section]:pt-10"
      id="thank-you"
    >
      <div className="flex items-start justify-between gap-16 max-[1199px]:flex-col max-[1199px]:items-stretch max-[1199px]:gap-12">
        <div
          className="flex min-w-0 flex-col gap-5 rounded-lg border border-[--q-border] bg-[--q-paper] p-9 [flex:2_1_0%] max-[1199px]:basis-auto max-[767px]:px-5 max-[767px]:py-6"
          data-reveal
        >
          <div className="flex flex-col gap-1.5">
            <RichTextContent path="thankyou.eyebrow" as="div" className="eyebrow" />
            <RichTextContent
              path="thankyou.title"
              as="h2"
              className="m-0 font-heading text-[36px] font-light leading-[42px] tracking-[--q-tracking-tight] [&_p]:m-0 max-[767px]:text-[28px] max-[767px]:leading-[34px]"
            />
          </div>
          <RichTextContent
            path="thankyou.message"
            as="div"
            className="m-0 max-w-[600px] text-lg leading-7 text-[--q-muted] max-[767px]:text-[15px] max-[767px]:leading-[23px] [&_p]:m-0"
          />
          <div className="flex gap-3 pt-2 max-[767px]:flex-col max-[767px]:gap-2.5">
            <EditableSlotGroup groupPath="thankyou.plans_cta" definition={plansCtaDef}>
              <a className="btn btn-primary" href={plans.url || PATHS.floorPlans} target={plans._target || '_self'}>
                {plans.label || 'View Floor Plans'} <span className="btn__arrow">→</span>
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="thankyou.tour_cta" definition={tourCtaDef}>
              <a
                className="btn btn-secondary"
                href={tourLink.href}
                target={tourLink.isTour ? tour._target || '_self' : '_self'}
                data-vibesite-event={tourLink.isTour ? tour.vibesite_event || undefined : undefined}
              >
                {tourLink.label}
              </a>
            </EditableSlotGroup>
          </div>
        </div>

        <div
          className="flex min-w-0 flex-col gap-9 pt-3 [flex:1_1_0%] max-[1199px]:basis-auto max-[767px]:gap-7 max-[767px]:pt-0"
          data-reveal
          style={stagger(1)}
        >
          <div className="flex flex-col gap-4">
            <RichTextContent path="thankyou.office_label" as="div" className="label [&_p]:m-0" />
            {street && (
              <div className="-mb-3 font-heading text-[28px] leading-[34px] tracking-[--q-tracking-tight] max-[767px]:text-[24px] max-[767px]:leading-[30px]">{street}</div>
            )}
            {city && <div className="text-base leading-[26px] text-[--q-muted]">{city}</div>}
            {address && (
              <a
                className="textlink self-start [&.textlink]:inline-flex [&.textlink]:min-h-6 [&.textlink]:items-center [&.textlink]:text-[15px] max-[767px]:[&.textlink]:min-h-11"
                href={directions}
                target="_blank"
                rel="noopener"
              >
                Get Directions
              </a>
            )}
          </div>
          {tel && (
            <div className="flex flex-col gap-3.5 border-t border-t-[--q-border] pt-6">
              <div className={infoRow}>
                <span>Phone</span>
                <a className={`dniphone dniphonehref ${infoLink}`} href={tel}>{flags.phone}</a>
              </div>
            </div>
          )}
          <EditableCollectionBinding
            bindingPath="thankyou.hours"
            label="Office Hours"
            className="flex flex-col gap-3 border-t border-t-[--q-border] pt-6 empty:hidden"
          >
            {rows.length > 0 && (
              <>
                <RichTextContent path="thankyou.hours_label" as="div" className="label [&_p]:m-0" />
                {rows.map(([d, t]) => (
                  <div className="flex justify-between gap-4" key={d}>
                    <span className="font-heading text-[22px] leading-[30px]">{d}</span>
                    <span className="text-base leading-[30px] text-[--q-muted]">{t}</span>
                  </div>
                ))}
              </>
            )}
          </EditableCollectionBinding>
        </div>
      </div>

      {mapSrc && (
        <div className="mt-[72px] h-[420px] overflow-hidden rounded-lg bg-[--q-surface] max-[767px]:mt-10 max-[767px]:h-[300px]" data-reveal>
          <iframe className="block h-full w-full [border:0]" src={mapSrc} title={`Map to ${address}`} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </div>
      )}
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ThankYou',
  displayName: 'Thank You',
  tokens: {
    'thankyou.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Message received</p>' },
    'thankyou.title': { label: 'Title', type: 'richtext', defaultValue: '<p>Thanks for reaching out</p>' },
    'thankyou.message': {
      label: 'Message',
      type: 'richtext',
      defaultValue: '<p>Thank you for your interest! A team member will reach out to you soon to help you find your dream home.</p>',
    },
    'thankyou.office_label': { label: 'Office Label', type: 'richtext', defaultValue: '<p>Leasing office</p>' },
    'thankyou.hours_label': { label: 'Hours Label', type: 'richtext', defaultValue: '<p>Hours</p>' },
  },
  slotGroups: {
    'thankyou.plans_cta': plansCtaDef,
    'thankyou.tour_cta': tourCtaDef,
  },
  collectionBindings: {
    'thankyou.hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ThankYou as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ThankYou;
